import { useMemo } from 'react';

import type { BalanceType } from '../../api/fetchers/userInfo/types';
import { roundNumber } from '../../utils/number';
import { useBetSlice } from '../slices/bets/selectors';
import { useGameStateSlice } from '../slices/game/selectors';

/**
 * Computes the balance and bonus based on the given parameters.
 *
 * @param {BalanceType} balance - The current balance of the user.
 * @param {number} totalBet - The total bet amount.
 * @param {number} digitsNumber - The number of digits to round the balance and bonus to.
 * @param {number} [winResult] - The win result amount. Optional.
 *
 * @returns {BalanceType} - The new balance and bonus.
 */
export const computeBalance = (
  balance: BalanceType,
  totalBet: number,
  digitsNumber: number,
  winResult?: number
): BalanceType => {
  const balanceDiff = balance.amount - totalBet;
  const bonusDiff = Math.min(balanceDiff, 0);
  const newBonus = balance.bonus + bonusDiff;
  let newBalance = Math.max(balanceDiff, 0);
  if (winResult) {
    newBalance += winResult;
  }
  return {
    ...balance,
    amount: roundNumber(newBalance, digitsNumber),
    bonus: roundNumber(newBonus, digitsNumber),
  };
};

/**
 * useCompositeBalance - get correct balance for UI to display
 *
 * does not update the value during active game rounds or when bets are closed or not allowed
 *
 * does update balance when making bets or win result balance request is taking too long
 */
export const useCompositeBalance = (): BalanceType | undefined => {
  const balance = useGameStateSlice.balance();
  const digitsNumber = useGameStateSlice.getCurrencyInfo()?.digitsNumber ?? 0;
  const betTimestamp = useBetSlice.getUpdateTimestamp();
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const isLoadedInActiveRound = useGameStateSlice.isLoadedInActiveRound();
  const totalBet = useBetSlice.totalBet();
  const winResult = useGameStateSlice.winResult();

  return useMemo(() => {
    if (!balance) return;

    const balanceTimestamp = balance.updateTimestamp;
    const winResultTimestamp = winResult?.updateTimestamp ?? 0;
    /**
     * isLoadedInActiveRound - the server is sending in balance with the deducted bet in case of restore, so no need to do it manually as we do it otherwise.
     * isBetsOpenStatus - extra check for game state that the bets are actually closed and balance response from the server already should have the correct balance in response.
     * winResult - if we get in state where we have a win result, but the balance request has completed sooner and already should have the correct balance in response.
     */
    if (
      isLoadedInActiveRound ||
      (!isBetsOpenStatus && betTimestamp < balanceTimestamp) ||
      (winResult && winResultTimestamp < balanceTimestamp)
    ) {
      return balance;
    }

    return computeBalance(
      balance,
      totalBet,
      digitsNumber,
      /**
       * If the win result is the freshest of actions that affect balance - add it to the new balance amount (without subtracting totalBet).
       */
      winResult && winResultTimestamp > balanceTimestamp ? winResult.amount : undefined
    );
  }, [balance, betTimestamp, digitsNumber, isBetsOpenStatus, isLoadedInActiveRound, totalBet, winResult]);
};
